import React, { useState, useEffect } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  Routes,
  Route,
} from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { useAuth } from "../context/AuthContext";
import { getAllOrders } from "../utils/orderApi";
import { createPaymobPayment } from "../utils/paymobApi";
import { useCart } from "../context/CartContext";
import { toggleFavoriteProduct } from "../utils/wishlistApi";
import GoTo from "../components/GoTo";
import { useLanguage } from "../context/LanguageContext";
import { getProductLink } from "../utils/productLink";

function AccountPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem, open } = useCart();
  const { language } = useLanguage();

  // Helper function to add language prefix to paths
  const getLocalizedPath = (path) => {
    // Split path and query string
    const [pathPart, queryString] = path.split("?");

    if (language === "ar") {
      // Remove /ar if already present to avoid duplication
      const cleanPath = pathPart.startsWith("/ar")
        ? pathPart.replace("/ar", "")
        : pathPart;
      const localizedPath = `/ar${cleanPath === "/" ? "" : cleanPath}`;
      return queryString ? `${localizedPath}?${queryString}` : localizedPath;
    }
    // Remove /ar prefix if present when switching to English
    const englishPath = pathPart.startsWith("/ar")
      ? pathPart.replace("/ar", "") || "/"
      : pathPart;
    return queryString ? `${englishPath}?${queryString}` : englishPath;
  };
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payingOrderId, setPayingOrderId] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate(getLocalizedPath("/account-login"));
      return;
    }

    // Fetch orders if on orders page
    if (location.pathname.includes("/account/orders")) {
      fetchOrders();
    }
  }, [token, navigate, location.pathname, language]);

  const displayName = user?.name || user?.login || "User";
  const email = user?.login || user?.email || "";

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
    } finally {
      navigate(getLocalizedPath("/account-login"));
    }
  };

  // Orders functions
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");
      const ordersData = await getAllOrders(token);
      setOrders(ordersData);
    } catch (err) {
      setError(err?.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      let date;
      if (typeof dateString === "string") {
        date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return dateString;
        }
      } else {
        date = new Date(dateString);
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString || "N/A";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0.00 EGP";
    return `${parseFloat(amount).toFixed(2)} EGP`;
  };

  const getStatusBadge = (state) => {
    if (language === "ar") {
      const statusMap = {
        draft: "مسودة",
        sent: "مرسل",
        sale: "مؤكد",
        done: "منجز",
        cancel: "ملغي",
        "on hold": "معلق",
      };
      return statusMap[state] || state || "غير معروف";
    }
    const statusMap = {
      draft: "Draft",
      sent: "Sent",
      sale: "Confirmed",
      done: "Done",
      cancel: "Cancelled",
      "on hold": "On Hold",
    };
    return statusMap[state] || state || "Unknown";
  };

  const getStatusClass = (state) => {
    const classMap = {
      draft: "text-warning",
      sent: "text-info",
      sale: "text-success",
      done: "text-success",
      cancel: "text-danger",
      "on hold": "text-warning",
    };
    return classMap[state] || "text-muted";
  };

  const getPaymentStatus = (isPaid) => {
    if (language === "ar") {
      return isPaid ? "مدفوع" : "قيد الانتظار";
    }
    return isPaid ? "Paid" : "Pending";
  };

  const getPaymentClass = (isPaid) => {
    return isPaid ? "text-success" : "text-warning";
  };

  const handlePayNow = async (order) => {
    if (!order || !user) return;

    setPayingOrderId(order.id);
    setError("");

    try {
      const userName = user?.name || user?.user_name || "";
      const splittedUserName = userName.split(" ");
      const firstName = splittedUserName[0] || "";
      const lastName = splittedUserName.length > 1 ? splittedUserName[1] : "";

      const paymobUrl = await createPaymobPayment(
        order.id,
        order.total || 0,
        firstName,
        lastName,
        user?.phone || user?.mobile || "",
        user?.email || ""
      );

      window.location.href = paymobUrl;
    } catch (e) {
      setError(e?.message || "Failed to initiate payment");
    } finally {
      setPayingOrderId(null);
    }
  };

  // User data
  const addressCity = user?.address_city || "N/A";
  const addressLine = user?.address_line || "N/A";
  const contactAddress = user?.contact_address || "N/A";
  const userEmail = user?.login || user?.email || "N/A";
  const userMobile = user?.mobile || user?.phone || "N/A";

  // Load wishlist products from localStorage
  const [wishlistProducts, setWishlistProducts] = useState(() => {
    try {
      const raw = localStorage.getItem("wishlist_items");
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Reload wishlist when component mounts or when navigating to wishlist section
  useEffect(() => {
    const loadWishlist = () => {
      try {
        const raw = localStorage.getItem("wishlist_items");
        const items = raw ? JSON.parse(raw) : [];
        setWishlistProducts(items);
      } catch (e) {
        console.error("Failed to load wishlist from localStorage:", e);
        setWishlistProducts([]);
      }
    };

    loadWishlist();

    // Listen for storage events
    const handleStorageChange = (e) => {
      if (e.key === "wishlist_items") {
        loadWishlist();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);
  // Determine current section and title
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes("/account/orders")) return "orders";
    if (path.includes("/account/details")) return "details";
    if (path.includes("/account/address")) return "address";
    if (path.includes("/account/wishlist")) return "wishlist";
    return "dashboard";
  };

  const getPageTitle = () => {
    const section = getCurrentSection();
    if (language === "ar") {
      const titles = {
        dashboard: "حسابي",
        orders: "طلباتي",
        details: "تفاصيل الحساب",
        address: "العنوان",
        wishlist: "قائمة الأمنيات",
      };
      return titles[section] || "حسابي";
    }
    const titles = {
      dashboard: "My Account",
      orders: "My Orders",
      details: "Account Details",
      address: "Address",
      wishlist: "Wishlist",
    };
    return titles[section] || "My Account";
  };

  const currentSection = getCurrentSection();

  return (
    <div>
      <style>
        {`
          /* Account navigation icon styling */
          .my-account-nav-item {
            display: flex !important;
            align-items: center;
            gap: 12px;
          }
          
          .account-nav-icon {
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            flex-shrink: 0;
            transition: all 0.2s ease;
          }
          
          .account-nav-icon i {
            font-size: 18px;
            color: #0e5aa7;
          }
          
          /* Icon background colors */
          .account-nav-icon-dashboard {
            background: #E6F0FF;
          }
          
          .account-nav-icon-orders {
            background: #E6F0FF;
          }
          
          .account-nav-icon-details {
            background: #E6F0FF;
          }
          
          .account-nav-icon-address {
            background: #E6F0FF;
          }
          
          .account-nav-icon-wishlist {
            background: #FFE6F1;
          }
          
          .account-nav-icon-wishlist i {
            color: #D14343;
          }
          
          .account-nav-icon-logout {
            background: #FFE6E6;
          }
          
          .account-nav-icon-logout i {
            color: #D14343;
          }
          
          .account-nav-text {
            flex: 1;
          }
          
          /* Hover effects */
          .my-account-nav-item:hover .account-nav-icon {
            transform: scale(1.05);
          }
          
          .my-account-nav-item.active .account-nav-icon {
            background: #0e5aa7;
          }
          
          .my-account-nav-item.active .account-nav-icon i {
            color: #ffffff;
          }
          
          /* Show sidebar on mobile and hide toggle button */
          @media (max-width: 1149px) {
            .wrap-sidebar-account {
              display: block !important;
            }
            
            .btn-sidebar-account {
              display: none !important;
            }
            
            /* Hide offcanvas on mobile since we're showing sidebar directly */
            .offcanvas.canvas-sidebar-account {
              display: none !important;
            }
            
            .offcanvas-backdrop {
              display: none !important;
            }
            
            /* Make sidebar full width on mobile and stack above content */
            .col-lg-3 {
              width: 100% !important;
              margin-bottom: 20px;
            }
            
            .col-lg-9 {
              width: 100% !important;
            }
          }
        `}
      </style>
      <Header />
      <PageTitle title={getPageTitle()} />
      <section className="flat-spacing-11">
        <div className="container">
          <div className="row">
            <div className="col-lg-3">
              <div className="wrap-sidebar-account">
                <ul className="my-account-nav">
                  <li>
                    <Link
                      to={getLocalizedPath("/account")}
                      className={`my-account-nav-item ${
                        currentSection === "dashboard" ? "active" : ""
                      }`}
                    >
                      <span className="account-nav-icon account-nav-icon-dashboard">
                        <i className="icon icon-home"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "لوحة التحكم" : "Dashboard"}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={getLocalizedPath("/account/orders")}
                      className={`my-account-nav-item ${
                        currentSection === "orders" ? "active" : ""
                      }`}
                    >
                      <span className="account-nav-icon account-nav-icon-orders">
                        <i className="icon icon-car-order"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "الطلبات" : "Orders"}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={getLocalizedPath("/account/details")}
                      className={`my-account-nav-item ${
                        currentSection === "details" ? "active" : ""
                      }`}
                    >
                      <span className="account-nav-icon account-nav-icon-details">
                        <i className="icon icon-person"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "تفاصيل الحساب" : "Account Details"}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={getLocalizedPath("/account/address")}
                      className={`my-account-nav-item ${
                        currentSection === "address" ? "active" : ""
                      }`}
                    >
                      <span className="account-nav-icon account-nav-icon-address">
                        <i className="icon icon-place"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "العنوان" : "Address"}</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={getLocalizedPath("/account/wishlist")}
                      className={`my-account-nav-item ${
                        currentSection === "wishlist" ? "active" : ""
                      }`}
                    >
                      <span className="account-nav-icon account-nav-icon-wishlist">
                        <i className="icon icon-heart"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "قائمة الأمنيات" : "Wishlist"}</span>
                    </Link>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="my-account-nav-item"
                      onClick={handleLogout}
                    >
                      <span className="account-nav-icon account-nav-icon-logout">
                        <i className="icon icon-logout"></i>
                      </span>
                      <span className="account-nav-text">{language === "ar" ? "تسجيل الخروج" : "Logout"}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-9">
              <div className="my-account-content">
                {currentSection === "dashboard" && (
                  <div className="account-dashboard">
                    <div className="mb_60">
                      <h5 className="fw-5 mb_20">{language === "ar" ? `مرحباً ${displayName}` : `Hello ${displayName}`}</h5>
                      {statusMessage && (
                        <div className="text-success mb_10">
                          {statusMessage}
                        </div>
                      )}
                      {errorMessage && (
                        <div className="text-danger mb_10">{errorMessage}</div>
                      )}
                      <p>
                        {language === "ar" ? (
                          <>
                            من لوحة تحكم حسابك يمكنك عرض{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/orders")}>
                              طلباتك الأخيرة
                            </Link>
                            ، وإدارة{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/address")}>
                              عنوان الشحن والفوترة
                            </Link>
                            ، و{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/details")}>
                              تعديل كلمة المرور وتفاصيل الحساب
                            </Link>
                            .
                          </>
                        ) : (
                          <>
                            From your account dashboard you can view your{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/orders")}>
                              recent orders
                            </Link>
                            , manage your{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/address")}>
                              shipping and billing address
                            </Link>
                            , and{" "}
                            <Link className="text_primary" to={getLocalizedPath("/account/details")}>
                              edit your password and account details
                            </Link>
                            .
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {currentSection === "orders" && (
                  <div className="account-order">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">{language === "ar" ? "جاري التحميل..." : "Loading..."}</span>
                        </div>
                        <p className="mt-3">{language === "ar" ? "جاري تحميل الطلبات..." : "Loading orders..."}</p>
                      </div>
                    ) : (
                      <>
                        {error && (
                          <div className="alert alert-danger mb-4">
                            {error}
                            <button
                              className="btn btn-sm btn-outline-danger ms-2"
                              onClick={fetchOrders}
                            >
                              {language === "ar" ? "إعادة المحاولة" : "Retry"}
                            </button>
                          </div>
                        )}

                        {orders.length === 0 && !error ? (
                          <div className="text-center py-5">
                            <h5>{language === "ar" ? "لم يتم العثور على طلبات" : "No orders found"}</h5>
                            <p>{language === "ar" ? "لم تقم بتقديم أي طلبات بعد." : "You haven't placed any orders yet."}</p>
                            <Link
                              to={getLocalizedPath("/shop/default")}
                              className="btn btn-primary"
                            >
                              {language === "ar" ? "ابدأ التسوق" : "Start Shopping"}
                            </Link>
                          </div>
                        ) : (
                          <div className="wrap-account-order">
                            <table>
                              <thead>
                                <tr>
                                  <th className="fw-6">{language === "ar" ? "الطلب" : "Order"}</th>
                                  <th className="fw-6">{language === "ar" ? "التاريخ" : "Date"}</th>
                                  <th className="fw-6">{language === "ar" ? "الحالة" : "Status"}</th>
                                  <th className="fw-6">{language === "ar" ? "الدفع" : "Payment"}</th>
                                  <th className="fw-6">{language === "ar" ? "الإجمالي" : "Total"}</th>
                                  <th className="fw-6">{language === "ar" ? "الإجراءات" : "Actions"}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orders.map((order) => (
                                  <tr key={order.id} className="tf-order-item">
                                    <td>{order.name || `#${order.id}`}</td>
                                    <td>{formatDate(order.date)}</td>
                                    <td>
                                      <span
                                        className={getStatusClass(order.state)}
                                      >
                                        {getStatusBadge(order.state)}
                                      </span>
                                    </td>
                                    <td>
                                      <span
                                        className={getPaymentClass(
                                          order.is_paid
                                        )}
                                      >
                                        {getPaymentStatus(order.is_paid)}
                                      </span>
                                    </td>
                                    <td>
                                      {language === "ar" ? (
                                        <>
                                          {formatCurrency(order.total)} لـ{" "}
                                          {order.lines?.length || 0}{" "}
                                          {(order.lines?.length || 0) === 1 ? "عنصر" : "عناصر"}
                                        </>
                                      ) : (
                                        <>
                                          {formatCurrency(order.total)} for{" "}
                                          {order.lines?.length || 0} item
                                          {(order.lines?.length || 0) !== 1
                                            ? "s"
                                            : ""}
                                        </>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Link
                                          to={getLocalizedPath(`/account-orders-details/${order.id}`)}
                                          state={{ order }}
                                          className="tf-btn btn-fill animate-hover-btn rounded-0 justify-content-center"
                                        >
                                          <span>{language === "ar" ? "عرض" : "View"}</span>
                                        </Link>
                                        {!order.is_paid && (
                                          <button
                                            type="button"
                                            onClick={() => handlePayNow(order)}
                                            disabled={
                                              payingOrderId === order.id
                                            }
                                            className="tf-btn btn-line animate-hover-btn rounded-0 justify-content-center"
                                          >
                                            <span>
                                              {payingOrderId === order.id
                                                ? (language === "ar" ? "جاري المعالجة..." : "Processing...")
                                                : (language === "ar" ? "ادفع الآن" : "Pay Now")}
                                            </span>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {currentSection === "details" && (
                  <div className="account-dashboard">
                    <div className="mb_60">
                      <h5 className="fw-5 mb_20">{language === "ar" ? "معلومات الحساب" : "Account Information"}</h5>
                      {statusMessage && (
                        <div className="text-success mb_10">
                          {statusMessage}
                        </div>
                      )}
                      {errorMessage && (
                        <div className="text-danger mb_10">{errorMessage}</div>
                      )}

                      <div className="tf-grid-layout md-col-2 gap-15 mb_30">
                        <div className="item">
                          <div className="text-2 text_black-2">{language === "ar" ? "الاسم الكامل" : "Full Name"}</div>
                          <div className="text-2 mt_4 fw-6">{displayName}</div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "عنوان البريد الإلكتروني" : "Email Address"}
                          </div>
                          <div className="text-2 mt_4 fw-6">{userEmail}</div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "رقم الهاتف المحمول" : "Mobile Number"}
                          </div>
                          <div className="text-2 mt_4 fw-6">{userMobile}</div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "حالة الحساب" : "Account Status"}
                          </div>
                          <div className="text-2 mt_4 fw-6">
                            <span className="text-success">{language === "ar" ? "نشط" : "Active"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt_30">
                        <h6 className="fw-5 mb_15">{language === "ar" ? "أمان الحساب" : "Account Security"}</h6>
                        <p className="text-muted">
                          {language === "ar"
                            ? "حسابك محمي بتشفير معياري. لأي مخاوف أمنية، يرجى الاتصال بفريق الدعم لدينا."
                            : "Your account is secured with industry-standard encryption. For any security concerns, please contact our support team."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {currentSection === "address" && (
                  <div className="account-dashboard">
                    <div className="mb_60">
                      <h5 className="fw-5 mb_20">{language === "ar" ? "معلومات العنوان" : "Address Information"}</h5>
                      {statusMessage && (
                        <div className="text-success mb_10">
                          {statusMessage}
                        </div>
                      )}
                      {errorMessage && (
                        <div className="text-danger mb_10">{errorMessage}</div>
                      )}

                      <div className="tf-grid-layout md-col-2 gap-15 mb_30">
                        <div className="item">
                          <div className="text-2 text_black-2">{language === "ar" ? "المدينة" : "City"}</div>
                          <div className="text-2 mt_4 fw-6">{addressCity}</div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "سطر العنوان" : "Address Line"}
                          </div>
                          <div className="text-2 mt_4 fw-6">{addressLine}</div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "عنوان الاتصال" : "Contact Address"}
                          </div>
                          <div className="text-2 mt_4 fw-6">
                            {contactAddress}
                          </div>
                        </div>
                        <div className="item">
                          <div className="text-2 text_black-2">
                            {language === "ar" ? "حالة العنوان" : "Address Status"}
                          </div>
                          <div className="text-2 mt_4 fw-6">
                            <span className="text-success">{language === "ar" ? "متحقق" : "Verified"}</span>
                          </div>
                        </div>
                      </div>

                      <div className="mt_30">
                        <h6 className="fw-5 mb_15">{language === "ar" ? "ملاحظات العنوان" : "Address Notes"}</h6>
                        <p className="text-muted">
                          {language === "ar"
                            ? "معلومات العنوان هذه للقراءة فقط ولا يمكن تعديلها من خلال هذه الواجهة. لتحديث عنوانك، يرجى الاتصال بفريق الدعم لدينا."
                            : "This address information is read-only and cannot be edited through this interface. To update your address, please contact our support team."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {currentSection === "wishlist" && (
                  <div className="account-wishlist">
                    {wishlistProducts.length > 0 ? (
                      <div className="tf-grid-layout tf-col-3 wrapper-shop">
                        {wishlistProducts.map((p) => (
                          <div
                            key={p.id}
                            className="card-product grid"
                            data-availability={p.availability}
                            data-brand={p.brand}
                          >
                            <div className="card-product-wrapper">
                              <Link
                                to={getProductLink(p)}
                                state={{ product: p }}
                                className="product-img"
                              >
                                <img
                                  className="lazyload img-product"
                                  data-src={
                                    p.image ||
                                    `https://erp-elsalab.odoo.com/web/image/${p.id}`
                                  }
                                  src={
                                    p.image ||
                                    `https://erp-elsalab.odoo.com/web/image/${p.id}`
                                  }
                                  alt={p.title || p.name}
                                />
                                <img
                                  className="lazyload img-hover"
                                  data-src={
                                    p.image ||
                                    `https://erp-elsalab.odoo.com/web/image/${p.id}`
                                  }
                                  src={
                                    p.image ||
                                    `https://erp-elsalab.odoo.com/web/image/${p.id}`
                                  }
                                  alt={p.title || p.name}
                                />
                              </Link>
                              {p.list_price != null &&
                                p.price != null &&
                                Number(p.list_price) > 0 &&
                                Number(p.list_price) > Number(p.price) &&
                                Math.round(
                                  ((Number(p.list_price) - Number(p.price)) /
                                    Number(p.list_price)) *
                                    100
                                ) > 0 && (
                                  <div
                                    className="on-sale-wrap"
                                    style={{
                                      position: "absolute",
                                      top: 10,
                                      left: 10,
                                    }}
                                  >
                                    <div
                                      className="on-sale-item"
                                      style={{
                                        backgroundColor: "var(--primary)",
                                        color: "#fff",
                                      }}
                                    >
                                      {Math.round(
                                        ((Number(p.list_price) -
                                          Number(p.price)) /
                                          Number(p.list_price)) *
                                          100
                                      )}
                                      % OFF
                                    </div>
                                  </div>
                                )}
                              <div className="list-product-btn absolute-2">
                                <a
                                  href="#"
                                  className="box-icon bg_white quick-add tf-btn-loading"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    addItem({ ...p, qty: 1 });
                                    open();
                                  }}
                                >
                                  <span className="icon icon-bag"></span>
                                  <span className="tooltip">{language === "ar" ? "أضف إلى السلة" : "Add to cart"}</span>
                                </a>
                                <button
                                  type="button"
                                  className="box-icon bg_white wishlist btn-icon-action active"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    if (!token) {
                                      navigate(getLocalizedPath("/account-login"));
                                      return;
                                    }
                                    try {
                                      await toggleFavoriteProduct(
                                        parseInt(p.id),
                                        false,
                                        token,
                                        p
                                      );
                                      // Update local state
                                      setWishlistProducts((prev) =>
                                        prev.filter((item) => item.id !== p.id)
                                      );
                                    } catch (error) {
                                      console.error(
                                        "Failed to remove from wishlist:",
                                        error
                                      );
                                      alert(
                                        error.message ||
                                          (language === "ar" ? "فشل إزالة من قائمة الأمنيات" : "Failed to remove from wishlist")
                                      );
                                    }
                                  }}
                                >
                                  <span className="icon icon-heart"></span>
                                  <span className="tooltip">
                                    {language === "ar" ? "إزالة من قائمة الأمنيات" : "Remove from Wishlist"}
                                  </span>
                                  <span className="icon icon-delete"></span>
                                </button>
                              </div>
                            </div>
                            <div className="card-product-info">
                              <Link
                                to={getProductLink(p)}
                                state={{ product: p }}
                                className="title link"
                              >
                                {p.title || p.name}
                              </Link>
                              {p.price != null && (
                                <div className="price-area">
                                  <span
                                    className="price current-price"
                                    style={{ color: "var(--primary)" }}
                                  >
                                    EGP {Number(p.price).toFixed(1)}
                                  </span>
                                  {p.list_price != null &&
                                    Number(p.list_price) > Number(p.price) && (
                                      <span
                                        className="price compare-at-price"
                                        style={{
                                          color: "#000",
                                          marginLeft: 8,
                                        }}
                                      >
                                        {Math.round(Number(p.list_price))}
                                      </span>
                                    )}
                                </div>
                              )}
                              {p.colors && p.colors.length > 0 && (
                                <div className="list-color-product">
                                  {p.colors.map((c) => (
                                    <span
                                      key={c}
                                      className="list-color-item color-swatch"
                                    >
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {p.sizes && p.sizes.length > 0 && (
                                <div className="size-list">
                                  {p.sizes.map((s) => (
                                    <span key={s} className="size-item">
                                      {s}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: "40px" }}>
                        <p>{language === "ar" ? "قائمة أمنياتك فارغة." : "Your wishlist is empty."}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sidebar */}
      {isSidebarOpen && <div className="offcanvas-backdrop fade show"></div>}
      <div
        className={`offcanvas offcanvas-start canvas-sidebar-account ${
          isSidebarOpen ? "show" : ""
        }`}
        style={{ display: isSidebarOpen ? "block" : "none" }}
      >
        <div className="canvas-wrapper">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">{language === "ar" ? "حسابي" : "My Account"}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setIsSidebarOpen(false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            <ul className="my-account-nav">
              <li>
                <Link
                  to={getLocalizedPath("/account")}
                  className={`my-account-nav-item ${
                    currentSection === "dashboard" ? "active" : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {language === "ar" ? "لوحة التحكم" : "Dashboard"}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedPath("/account/orders")}
                  className={`my-account-nav-item ${
                    currentSection === "orders" ? "active" : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {language === "ar" ? "الطلبات" : "Orders"}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedPath("/account/details")}
                  className={`my-account-nav-item ${
                    currentSection === "details" ? "active" : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {language === "ar" ? "تفاصيل الحساب" : "Account Details"}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedPath("/account/address")}
                  className={`my-account-nav-item ${
                    currentSection === "address" ? "active" : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {language === "ar" ? "العنوان" : "Address"}
                </Link>
              </li>
              <li>
                <Link
                  to={getLocalizedPath("/account/wishlist")}
                  className={`my-account-nav-item ${
                    currentSection === "wishlist" ? "active" : ""
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  {language === "ar" ? "قائمة الأمنيات" : "Wishlist"}
                </Link>
              </li>
              <li>
                <a
                  href="#"
                  className="my-account-nav-item"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsSidebarOpen(false);
                    handleLogout(e);
                  }}
                >
                  {language === "ar" ? "تسجيل الخروج" : "Logout"}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="btn-sidebar-account">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-controls="offcanvas"
          aria-label="Toggle sidebar"
        >
          <i className="icon icon-sidebar-2"></i>
        </button>
      </div>
      <Footer />
      <GoTo />
    </div>
  );
}

export default AccountPage;
